// pages/ResetPassword.tsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Eye,
  EyeOff,
  CheckCircle,
  Lock,
  ArrowRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useResetPassword } from "../../lib/api/authOnboarding";
import logo from "../../assets/logo.png";
import signinImg from "../../assets/signin.png";

// Validation Schema
const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(6, "Password must be at least 6 characters")
      .max(50, "Password too long"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;

export default function ResetPassword() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const navigate = useNavigate();
  const { id, token } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  // const [errorMsg, setErrorMsg] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const { mutateAsync: resetPassword, isPending } = useResetPassword();

  const onSubmit = async (data: ResetPasswordForm) => {
    // setErrorMsg("");
    try {
      await resetPassword({
        id: id as string,
        token: token as string,
        password: data.newPassword,
      });
      setSuccess(true);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.fields?.[0]?.message ||
        error.response?.data?.message ||
        "Something went wrong. Please try again.";
      console.warn(errorMessage);
      // setErrorMsg(errorMessage);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Image (Fixed) */}
      <div className="hidden lg:block lg:w-1/2 xl:w-[55%] relative h-screen">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/90 to-[#E05A6A]/90 z-10" />

        {/* Image */}
        <img
          src={signinImg}
          alt="Reset Password"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Content Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col justify-between p-12 xl:p-16">
          <div></div>
          <div className="max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              {success ? "Password Updated!" : "Create New Password"}
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              {success
                ? "Your password has been successfully reset. You can now log in with your new credentials."
                : "Choose a strong password to keep your account secure. Make sure it's at least 6 characters long."}
            </p>
          </div>
          <div className="flex items-center gap-8">
            <div>
              <div className="text-3xl font-bold text-white">50K+</div>
              <div className="text-white/70 text-sm">Contributors</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">10+</div>
              <div className="text-white/70 text-sm">Countries</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">$1M+</div>
              <div className="text-white/70 text-sm">Paid Out</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="flex-1 flex flex-col bg-white h-screen">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 lg:p-8 border-b border-gray-100">
          <Link to={FRONTEND_URL as string}>
            <img src={logo} alt="Unimerg" className="h-9 w-auto" />
          </Link>
        </div>

        {/* Content Container */}
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
          <div className="flex items-center justify-center min-h-full px-6 lg:px-12 xl:px-20 py-8">
            <div className="w-full max-w-sm">
              {success ? (
                /* Success View */
                <div className="text-center">
                  {/* Success Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30 mb-8">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>

                  {/* Header */}
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    Password Reset Successful
                  </h1>
                  <p className="text-gray-500 leading-relaxed mb-8">
                    Your password has been updated successfully. You can now log
                    in with your new password.
                  </p>

                  {/* Go to Login Button */}
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group"
                  >
                    Proceed to Login
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              ) : (
                /* Form View */
                <div>
                  {/* Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center shadow-lg shadow-[#8B52FF]/30 mb-8">
                    <Lock className="w-10 h-10 text-white" />
                  </div>

                  {/* Header */}
                  <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                      Reset Password
                    </h1>
                    <p className="text-gray-500 leading-relaxed">
                      Enter your new password below to secure your account.
                    </p>
                  </div>

                  {/* Error Message */}
                  {/* {errorMsg && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-600 text-sm">{errorMsg}</p>
                    </div>
                  )} */}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {/* New Password Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showPassword ? "text" : "password"}
                          {...register("newPassword")}
                          placeholder="••••••••"
                          className={`w-full pl-12 pr-12 py-3.5 rounded-xl border-2 
                          ${
                            errors.newPassword
                              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                              : "border-gray-200 focus:border-[#8B52FF] focus:ring-[#8B52FF]/20"
                          }
                          bg-white text-gray-900 placeholder-gray-400
                          focus:outline-none focus:ring-4
                          transition-all duration-200`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {errors.newPassword && (
                        <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.newPassword.message}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          {...register("confirmPassword")}
                          placeholder="••••••••"
                          className={`w-full pl-12 pr-12 py-3.5 rounded-xl border-2 
                          ${
                            errors.confirmPassword
                              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                              : "border-gray-200 focus:border-[#8B52FF] focus:ring-[#8B52FF]/20"
                          }
                          bg-white text-gray-900 placeholder-gray-400
                          focus:outline-none focus:ring-4
                          transition-all duration-200`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>

                    {/* Password Requirements */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Password requirements:
                      </p>
                      <ul className="text-sm text-gray-500 space-y-1">
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          At least 6 characters long
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          Maximum 50 characters
                        </li>
                      </ul>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isPending}
                      className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Resetting Password...
                        </>
                      ) : (
                        <>
                          Reset Password
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to Login Link */}
                  <div className="mt-8 text-center">
                    <Link
                      to="/login"
                      className="text-gray-500 hover:text-[#8B52FF] font-medium transition-colors"
                    >
                      Back to Login
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
